import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { agencyService } from '../../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Bot } from 'lucide-react';

const AdminAgencyFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    plan: 'pro',
    publicProfile: {
      displayName: '',
      description: '',
      addressText: '',
      phonePublic: '',
      emailPublic: '',
      social: {
        instagram: '',
        facebook: '',
        website: ''
      }
    },
    privateProfile: {
      legalName: '',
      taxId: '',
      emailAdmin: ''
    },
    settings: {
      currency: 'ARS',
      timezone: 'America/Argentina/Buenos_Aires',
      locale: 'es-AR'
    },
    agent_webhook_url: ''
  });

  // Admin user data (only for new agencies)
  const [adminData, setAdminData] = useState({
    email: '',
    password: '',
    name: ''
  });

  useEffect(() => {
    if (isEdit) {
      loadAgency();
    }
  }, [id]);

  const loadAgency = async () => {
    try {
      const agency = await agencyService.getById(id);
      setFormData({
        name: agency.name || '',
        slug: agency.slug || '',
        plan: agency.plan || 'pro',
        publicProfile: {
          displayName: agency.publicProfile?.displayName || agency.name || '',
          description: agency.publicProfile?.description || '',
          addressText: agency.publicProfile?.addressText || '',
          phonePublic: agency.publicProfile?.phonePublic || '',
          emailPublic: agency.publicProfile?.emailPublic || '',
          social: {
            instagram: agency.publicProfile?.social?.instagram || '',
            facebook: agency.publicProfile?.social?.facebook || '',
            website: agency.publicProfile?.social?.website || ''
          }
        },
        privateProfile: {
          legalName: agency.privateProfile?.legalName || '',
          taxId: agency.privateProfile?.taxId || '',
          emailAdmin: agency.privateProfile?.emailAdmin || ''
        },
        settings: agency.settings || {
          currency: 'ARS',
          timezone: 'America/Argentina/Buenos_Aires',
          locale: 'es-AR'
        },
        agent_webhook_url: agency.agent_webhook_url || ''
      });
    } catch (error) {
      toast.error('Error al cargar agencia');
      navigate('/app/admin/agencies');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit) {
        await agencyService.update(id, formData);
        toast.success('Agencia actualizada');
      } else {
        // Validate admin data
        if (!adminData.email || !adminData.password || !adminData.name) {
          toast.error('Completa los datos del administrador');
          setLoading(false);
          return;
        }
        
        await agencyService.create(formData, adminData.email, adminData.password, adminData.name);
        toast.success('Agencia creada con su administrador');
      }
      navigate('/app/admin/agencies');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900">
          {isEdit ? 'Editar Agencia' : 'Nueva Agencia'}
        </h1>
        {!isEdit && (
          <p className="text-slate-600 mt-2">
            Al crear la agencia se generará automáticamente un usuario administrador y un canal de WhatsApp.
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Admin User Section (only for new agencies) */}
        {!isEdit && (
          <Card className="mb-6 border-blue-200 bg-blue-50/30">
            <CardHeader>
              <CardTitle className="text-blue-800">Administrador de la Agencia</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-blue-600 mb-4">
                Este usuario podrá gestionar la agencia, vehículos y usuarios.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Nombre del Admin *</label>
                  <Input
                    value={adminData.name}
                    onChange={(e) => setAdminData({ ...adminData, name: e.target.value })}
                    placeholder="Juan Pérez"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Email del Admin *</label>
                  <Input
                    type="email"
                    value={adminData.email}
                    onChange={(e) => setAdminData({ ...adminData, email: e.target.value })}
                    placeholder="admin@agencia.com"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Contraseña *</label>
                  <Input
                    type="password"
                    value={adminData.password}
                    onChange={(e) => setAdminData({ ...adminData, password: e.target.value })}
                    placeholder="••••••••"
                    minLength={6}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Basic Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Información Básica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Nombre de la Agencia *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData({ 
                      ...formData, 
                      name,
                      slug: isEdit ? formData.slug : generateSlug(name),
                      publicProfile: {
                        ...formData.publicProfile,
                        displayName: formData.publicProfile.displayName || name
                      }
                    });
                  }}
                  placeholder="Premium Motors"
                  required
                  data-testid="name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Plan</label>
                <Select 
                  value={formData.plan} 
                  onValueChange={(value) => setFormData({ ...formData, plan: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Slug (URL única) *</label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="premium-motors"
                pattern="^[a-z0-9-]+$"
                required
                disabled={isEdit}
                data-testid="slug-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Solo letras minúsculas, números y guiones. Este será la URL pública: /a/{formData.slug || 'slug'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Public Profile */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Perfil Público</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Nombre a mostrar</label>
              <Input
                value={formData.publicProfile.displayName}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  publicProfile: { ...formData.publicProfile, displayName: e.target.value }
                })}
                placeholder="Premium Motors S.A."
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Descripción</label>
              <Textarea
                value={formData.publicProfile.description}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  publicProfile: { ...formData.publicProfile, description: e.target.value }
                })}
                placeholder="Describe la agencia para los visitantes..."
                rows={3}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Dirección</label>
              <Input
                value={formData.publicProfile.addressText}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  publicProfile: { ...formData.publicProfile, addressText: e.target.value }
                })}
                placeholder="Av. del Libertador 1234, Buenos Aires"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Teléfono Público</label>
                <Input
                  value={formData.publicProfile.phonePublic}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    publicProfile: { ...formData.publicProfile, phonePublic: e.target.value }
                  })}
                  placeholder="+54 11 1234-5678"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Email Público</label>
                <Input
                  type="email"
                  value={formData.publicProfile.emailPublic}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    publicProfile: { ...formData.publicProfile, emailPublic: e.target.value }
                  })}
                  placeholder="ventas@agencia.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Instagram</label>
                <Input
                  value={formData.publicProfile.social.instagram}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    publicProfile: { 
                      ...formData.publicProfile, 
                      social: { ...formData.publicProfile.social, instagram: e.target.value }
                    }
                  })}
                  placeholder="https://instagram.com/agencia"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Facebook</label>
                <Input
                  value={formData.publicProfile.social.facebook}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    publicProfile: { 
                      ...formData.publicProfile, 
                      social: { ...formData.publicProfile.social, facebook: e.target.value }
                    }
                  })}
                  placeholder="https://facebook.com/agencia"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Website</label>
                <Input
                  value={formData.publicProfile.social.website}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    publicProfile: { 
                      ...formData.publicProfile, 
                      social: { ...formData.publicProfile.social, website: e.target.value }
                    }
                  })}
                  placeholder="https://www.agencia.com"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Private Profile */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Datos Privados (Facturación)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Razón Social</label>
                <Input
                  value={formData.privateProfile.legalName}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    privateProfile: { ...formData.privateProfile, legalName: e.target.value }
                  })}
                  placeholder="Premium Motors S.A."
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">CUIT</label>
                <Input
                  value={formData.privateProfile.taxId}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    privateProfile: { ...formData.privateProfile, taxId: e.target.value }
                  })}
                  placeholder="30-12345678-9"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Email Administrativo</label>
                <Input
                  type="email"
                  value={formData.privateProfile.emailAdmin}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    privateProfile: { ...formData.privateProfile, emailAdmin: e.target.value }
                  })}
                  placeholder="admin@agencia.com"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Agente IA */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="w-4 h-4 text-violet-600" />
              Webhook Agente IA
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-slate-500">
              URL del webhook de n8n que recibe el toggle del agente. Al activar o desactivar
              el agente en un lead, el backend llama a esta URL con el{' '}
              <code className="text-xs bg-slate-100 px-1 rounded">chatwoot_conversation_id</code>.
            </p>
            <Input
              value={formData.agent_webhook_url}
              onChange={(e) => setFormData({ ...formData, agent_webhook_url: e.target.value })}
              placeholder="https://n8n.tudominio.com/webhook/autoflow-agent-toggle"
              className="font-mono text-sm"
            />
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button type="submit" disabled={loading} data-testid="submit-button">
            {loading ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear Agencia'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/app/admin/agencies')}
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminAgencyFormPage;
